// src/components/JukeboxPicker.js
//
// Bottom-sheet modal for the Jukebox feature. Two-level interaction:
//   1. Horizontal sound pills — pick which jukebox-enabled sound to configure
//   2. Version list — pick which version of that sound to play
//
// Selecting a version immediately applies the change (with crossfade if the
// sound is live). The Done button closes the sheet.

import React, { useState, useEffect } from 'react';
import {
  Modal,
  View,
  Text,
  Pressable,
  ScrollView,
  StyleSheet,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Colors, Radius, Fonts, Typography } from '../theme';
import { useJukebox } from '../context/JukeboxContext';

export default function JukeboxPicker({ visible, onClose, sounds, tabId }) {
  const { getActiveVersionId, setVersion } = useJukebox();

  // Track which sound is focused in the picker; default to first sound
  const [focusedSoundId, setFocusedSoundId] = useState(sounds[0]?.id ?? null);

  // Reset to first sound each time the modal opens
  useEffect(() => {
    if (visible) setFocusedSoundId(sounds[0]?.id ?? null);
  }, [visible, sounds]);

  const focusedSound = sounds.find((s) => s.id === focusedSoundId);

  const handleVersionSelect = (sound, version) => {
    setVersion(tabId, sound, version);
  };

  return (
    <Modal
      visible={visible}
      animationType="slide"
      transparent
      onRequestClose={onClose}
      statusBarTranslucent
    >
      {/* Tap outside to dismiss */}
      <Pressable style={styles.backdrop} onPress={onClose}>
        {/* Prevent taps inside the sheet from closing it */}
        <Pressable style={styles.sheet} onPress={() => {}}>

          {/* Drag handle */}
          <View style={styles.handle} />

          {/* ── Header ─────────────────────────────────────────────── */}
          <View style={styles.header}>
            <View style={styles.headerLeft}>
              <View style={styles.titleIcon}>
                <Ionicons name="radio" size={15} color={Colors.accent} />
              </View>
              <Text style={styles.title}>Jukebox</Text>
              <View style={styles.freeBadge}>
                <Text style={styles.freeBadgeText}>FREE</Text>
              </View>
            </View>
            <Pressable onPress={onClose} hitSlop={14} style={styles.closeBtn}>
              <Ionicons name="close" size={20} color={Colors.textMuted} />
            </Pressable>
          </View>

          <Text style={styles.subtitle}>
            Choose a sound, then pick which version plays.
          </Text>
          <View style={styles.hintRow}>
            <Ionicons name="information-circle-outline" size={13} color={Colors.textMuted} style={{ marginTop: 1 }} />
            <Text style={styles.hintText}>
              Turn a sound on first, then swap its version here.
            </Text>
          </View>

          {/* ── Sound pills ────────────────────────────────────────── */}
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            style={styles.pillRow}
            contentContainerStyle={styles.pillRowContent}
          >
            {sounds.map((s) => {
              const active = focusedSoundId === s.id;
              return (
                <Pressable
                  key={s.id}
                  style={[styles.pill, active && styles.pillActive]}
                  onPress={() => setFocusedSoundId(s.id)}
                >
                  <Ionicons
                    name={s.icon}
                    size={14}
                    color={active ? Colors.textOnAccent : Colors.accent}
                  />
                  <Text style={[styles.pillText, active && styles.pillTextActive]}>
                    {s.name}
                  </Text>
                </Pressable>
              );
            })}
          </ScrollView>

          {/* ── Version list ───────────────────────────────────────── */}
          <View style={styles.versionCard}>
            {focusedSound?.versions.map((v, i) => {
              const storedId = getActiveVersionId(tabId, focusedSound.id);
              // First version is the implicit default when nothing is stored
              const isActive = storedId ? storedId === v.id : i === 0;
              const notLast = i < focusedSound.versions.length - 1;

              return (
                <Pressable
                  key={v.id}
                  style={[
                    styles.versionRow,
                    isActive && styles.versionRowActive,
                    notLast && styles.versionRowDivider,
                  ]}
                  onPress={() => handleVersionSelect(focusedSound, v)}
                >
                  {/* Radio button */}
                  <View style={[styles.radio, isActive && styles.radioActive]}>
                    {isActive && <View style={styles.radioDot} />}
                  </View>

                  {/* Label */}
                  <Text
                    style={[styles.versionLabel, isActive && styles.versionLabelActive]}
                  >
                    {v.label}
                  </Text>

                  {/* Active badge */}
                  {isActive && (
                    <View style={styles.activeBadge}>
                      <Text style={styles.activeBadgeText}>ACTIVE</Text>
                    </View>
                  )}
                </Pressable>
              );
            })}
          </View>

          {/* ── Done ───────────────────────────────────────────────── */}
          <Pressable style={styles.doneBtn} onPress={onClose}>
            <Text style={styles.doneBtnText}>Done</Text>
          </Pressable>

          {/* Safe-area spacer on iOS */}
          {Platform.OS === 'ios' && <View style={{ height: 20 }} />}
        </Pressable>
      </Pressable>
    </Modal>
  );
}

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.55)',
    justifyContent: 'flex-end',
  },
  sheet: {
    backgroundColor: Colors.surface,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    paddingHorizontal: 20,
    paddingTop: 12,
    paddingBottom: 28,
    // Subtle top border for definition against the backdrop
    borderTopWidth: 1,
    borderColor: Colors.border,
  },

  handle: {
    width: 36,
    height: 4,
    borderRadius: 2,
    backgroundColor: Colors.border,
    alignSelf: 'center',
    marginBottom: 18,
  },

  // Header
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 6,
  },
  headerLeft: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  titleIcon: {
    width: 28,
    height: 28,
    borderRadius: Radius.sm,
    backgroundColor: Colors.accent + '20',
    alignItems: 'center',
    justifyContent: 'center',
  },
  title: {
    fontFamily: Fonts.bold,
    fontSize: 18,
    color: Colors.textBright,
  },
  freeBadge: {
    backgroundColor: Colors.accent + '22',
    borderRadius: Radius.pill,
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderWidth: 1,
    borderColor: Colors.accent + '44',
  },
  freeBadgeText: {
    fontFamily: Fonts.bold,
    fontSize: 10,
    color: Colors.accent,
    letterSpacing: 0.8,
  },
  closeBtn: {
    width: 32,
    height: 32,
    alignItems: 'center',
    justifyContent: 'center',
  },

  subtitle: {
    fontFamily: Fonts.regular,
    fontSize: 13,
    color: Colors.textSecondary,
    marginBottom: 18,
  },

  // Hint row
  hintRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 5,
    backgroundColor: Colors.bg,
    borderRadius: Radius.sm,
    paddingVertical: 8,
    paddingHorizontal: 10,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: Colors.border,
  },
  hintText: {
    flex: 1,
    fontFamily: Fonts.regular,
    fontSize: 12,
    color: Colors.textMuted,
    lineHeight: 17,
  },

  // Sound pills
  pillRow: { marginBottom: 16 },
  pillRowContent: { gap: 8, paddingRight: 4 },
  pill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 8,
    paddingHorizontal: 14,
    borderRadius: Radius.pill,
    backgroundColor: Colors.surface2,
    borderWidth: 1,
    borderColor: Colors.border,
  },
  pillActive: {
    backgroundColor: Colors.accent,
    borderColor: Colors.accent,
  },
  pillText: {
    fontFamily: Fonts.medium,
    fontSize: 13,
    color: Colors.textSecondary,
  },
  pillTextActive: {
    color: Colors.textOnAccent,
    fontFamily: Fonts.bold,
  },

  // Version card
  versionCard: {
    backgroundColor: Colors.bg,
    borderRadius: Radius.lg,
    borderWidth: 1,
    borderColor: Colors.border,
    overflow: 'hidden',
    marginBottom: 20,
  },
  versionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingVertical: 14,
    paddingHorizontal: 16,
  },
  versionRowActive: {
    backgroundColor: Colors.accent + '10',
  },
  versionRowDivider: {
    borderBottomWidth: 1,
    borderBottomColor: Colors.border,
  },

  // Radio
  radio: {
    width: 18,
    height: 18,
    borderRadius: 9,
    borderWidth: 2,
    borderColor: Colors.textMuted,
    alignItems: 'center',
    justifyContent: 'center',
  },
  radioActive: { borderColor: Colors.accent },
  radioDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: Colors.accent,
  },

  versionLabel: {
    flex: 1,
    fontFamily: Fonts.medium,
    fontSize: 14,
    color: Colors.textSecondary,
  },
  versionLabelActive: {
    color: Colors.textBright,
    fontFamily: Fonts.semiBold,
  },

  activeBadge: {
    backgroundColor: Colors.accent + '22',
    borderRadius: Radius.pill,
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderWidth: 1,
    borderColor: Colors.accent + '44',
  },
  activeBadgeText: {
    fontFamily: Fonts.bold,
    fontSize: 9,
    color: Colors.accent,
    letterSpacing: 0.8,
  },

  // Done button
  doneBtn: {
    backgroundColor: Colors.accent,
    borderRadius: Radius.md,
    paddingVertical: 14,
    alignItems: 'center',
  },
  doneBtnText: {
    fontFamily: Fonts.bold,
    fontSize: 15,
    color: Colors.textOnAccent,
  },
});
